import React from 'react';
import { useNavigate } from 'react-router-dom';
import { StarIcon, HeartIcon, EyeIcon, PlayIcon } from 'lucide-react';
interface Product {
  id: number | string;
  title: string;
  description: string;
  price: number;
  category: string;
  subcategory?: string;
  image: string;
  rating: number;
  seller: string;
  platform?: string;
  licenseType?: string;
  useCase?: string;
  isFree?: boolean;
  format?: string;
  skillLevel?: string;
  industry?: string;
  creatorType?: string;
  isTrending?: boolean;
  isNewArrival?: boolean;
  dateAdded?: string;
  isVideo?: boolean;
  videoMetadata?: {
    width?: number;
    height?: number;
    isVertical?: boolean;
    duration?: number;
  };
}
export const ProductCard: React.FC<{
  product: Product;
}> = ({
  product
}) => {
  const navigate = useNavigate();
  const handlePreview = () => {
    navigate(`/product/${product.id}`);
  };
  const handleBuyNow = () => {
    navigate(`/product/${product.id}`);
  };
  // Determine if this is a video product and if it's vertical
  const isVideo = product.isVideo || product.category === 'Short-form Video';
  const isVerticalVideo = product.videoMetadata?.isVertical || false;
  return <div className="group relative bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300" data-id="element-1376">
      <div className={`relative ${isVideo ? isVerticalVideo ? 'video-preview-card vertical' : 'video-preview-card horizontal' : 'aspect-[4/3]'} overflow-hidden cursor-pointer`} onClick={handlePreview} data-id="element-1377">
        <img src={product.image} alt={product.title} className={`w-full h-full ${isVideo ? isVerticalVideo ? 'video-thumbnail vertical' : 'video-thumbnail horizontal' : 'object-cover'} transition-transform duration-400 ease-in-out group-hover:scale-105`} onError={e => {
        e.currentTarget.src = 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80';
      }} data-id="element-1378" />
        {/* Video play indicator */}
        {isVideo && <div className="absolute inset-0 flex items-center justify-center" data-id="element-1379">
            <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center" data-id="element-1380">
              <PlayIcon className="h-6 w-6 text-white" data-id="element-1381" />
            </div>
          </div>}
        <div className="absolute top-3 right-3 bg-white dark:bg-[#162825] rounded-full p-2 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-1382">
          <HeartIcon className="h-5 w-5 text-gray-500 dark:text-[#7A8F8A] hover:text-red-500 dark:hover:text-red-400 cursor-pointer" data-id="element-1383" />
        </div>
        {/* Category tag */}
        <div className="absolute top-3 left-3" data-id="element-1384">
          <span className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full" data-id="element-1385">
            {product.category}
          </span>
        </div>
        {/* Video Type indicator for video products */}
        {isVideo && <div className="absolute bottom-3 left-3" data-id="element-1386">
            <span className="bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-1387">
              {isVerticalVideo ? 'Vertical' : 'Horizontal'} Video
            </span>
          </div>}
        {/* Free badge */}
        {product.isFree && <div className="absolute bottom-3 left-3" data-id="element-1388">
            <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-1389">
              FREE
            </span>
          </div>}
        {/* Trending badge */}
        {product.isTrending && <div className="absolute bottom-3 right-3" data-id="element-1390">
            <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center" data-id="element-1391">
              <span className="mr-1" data-id="element-1392">🔥</span>Trending
            </span>
          </div>}
        {/* New badge */}
        {product.isNewArrival && !product.isTrending && <div className="absolute bottom-3 right-3" data-id="element-1393">
            <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center" data-id="element-1394">
              <span className="mr-1" data-id="element-1395">✨</span>New
            </span>
          </div>}
      </div>
      <div className="p-4" data-id="element-1396">
        <div className="flex items-center justify-between mb-1.5" data-id="element-1397">
          {/* Seller info */}
          <div className="flex items-center" data-id="element-1398">
            <div className="w-5 h-5 rounded-full bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] flex items-center justify-center text-white text-xs mr-2" data-id="element-1399">
              {product.seller.charAt(0)}
            </div>
            <span className="text-xs text-gray-600 dark:text-[#7A8F8A]" data-id="element-1400">
              by {product.seller}
            </span>
          </div>
          {/* Rating */}
          <div className="flex items-center" data-id="element-1401">
            <StarIcon className="h-3.5 w-3.5 text-yellow-400 fill-current" data-id="element-1402" />
            <span className="text-xs text-gray-600 dark:text-[#7A8F8A] ml-1" data-id="element-1403">
              {product.rating}
            </span>
          </div>
        </div>
        <h3 className="text-base font-semibold mb-1 text-gray-800 dark:text-[#F8FAF9] cursor-pointer line-clamp-1" onClick={handlePreview} data-id="element-1404">
          {product.title}
        </h3>
        <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mb-2.5 line-clamp-2" data-id="element-1405">
          {product.description}
        </p>
        <div className="flex flex-wrap gap-1.5 mb-2.5" data-id="element-1406">
          {product.platform && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-1407">
              {product.platform}
            </span>}
          {product.format && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-1408">
              {product.format}
            </span>}
          {product.skillLevel && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-1409">
              {product.skillLevel}
            </span>}
        </div>
        <div className="flex items-center justify-between" data-id="element-1410">
          <span className="text-base font-bold text-gray-900 dark:text-white" data-id="element-1411">
            {product.isFree ? 'Free' : `$${product.price}`}
          </span>
          <div className="flex space-x-1" data-id="element-1412">
            <button className="bg-[#0A3C52] dark:bg-[#0B3C45] text-white px-2.5 py-1.5 rounded-l-full text-xs font-medium flex items-center justify-center hover:bg-[#0D4B65] transition-colors" onClick={handlePreview} data-id="element-1413">
              <EyeIcon className="h-3 w-3 mr-1" data-id="element-1414" />
              Preview
            </button>
            <button className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-2.5 py-1.5 rounded-r-full text-xs font-medium hover:bg-[#25A077] transition-colors" onClick={handleBuyNow} data-id="element-1415">
              Buy Now
            </button>
          </div>
        </div>
      </div>
    </div>;
};